import cn from "classnames"
import { debounce } from "lodash-es"
import React, { useCallback } from "react"
import { withTranslation, type WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import type SignText from "../app/objects/sign_text"
import Blinker from "@/common/components/blinker"
import Scrollable from "@/common/components/scrollable"
import Slider from "@/common/components/slider"
import { isTablet, metaNameSign } from "@/common/util"
import Icons from "@/images/signomatic/objects"
import ShapeIcons from "@/images/signomatic/shapes"
import Sign from "@/signomatic/app/sign"
import { currentMinText } from "@/signomatic/app/sign_validator"
import ColorPicker from "@/signomatic/components/color_picker"
import Hint from "@/signomatic/components/hint"
import ObjectBorderPicker from "@/signomatic/components/object_border_picker"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import ColorUtil from "@/signomatic/util/colors"
import Fonts from "@/signomatic/util/fonts"
import { ChevronLeftLarge, ChevronRightLarge, TrashOutline, DuplicateOutline, PlusLarge } from "@/skylticons"
import TextLargeIcon from "@/skylticons/text_size_large"
import TextSmallIcon from "@/skylticons/text_size_small"

interface FontPreview {
  width: number
  height: number
  data: string
}

const fontPreviews: Record<string, FontPreview | undefined> = {}

interface FontItemProps {
  label: string
  value: string
  custom: boolean
  selected?: boolean
  onClick?: (value: string) => void
}

const FontItem: React.FC<FontItemProps> = ({ label, value, selected, custom, onClick }) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(value)
    }
  }, [onClick, value])

  let preview = fontPreviews[label]

  if (!preview) {
    const font = Fonts.getOpentypeFont(label === "Braille (SE 6p)" ? "Arial" : label, "normal")

    if (font) {
      const path = font.getPath(label, 0, 19, 19)
      const box = path.getBoundingBox()
      preview = fontPreviews[label] = {
        width: box.x2,
        height: box.y2,
        data: path.toPathData(2),
      }
    }
  }

  return (
    <div className={cn("font-item flex", label.replace(new RegExp(" ", "g"), ""), { selected })} onClick={handleClick}>
      {custom && <span className="mr-2">(Admin only)</span>}
      {preview ? (
        <svg width={preview.width} height={preview.height}>
          <path d={preview.data} />
        </svg>
      ) : (
        label
      )}
    </div>
  )
}

interface FontGroupProps {
  label: string
  items: FontItemProps[]
  onClick?: (value: string) => void
  classes?: string
}

const FontGroup: React.FC<FontGroupProps> = ({ label, items, onClick, classes }) => {
  return (
    <div className={cn("font-group", classes)}>
      <div className="paragraph-2xs font-header">{label}</div>
      {items.map((item, i) => (
        <FontItem key={i} onClick={onClick} {...item} />
      ))}
    </div>
  )
}

interface ButtonProps {
  name: string
  label?: string
  active?: boolean
  disabled?: boolean
  onClick: (e: React.FormEvent) => void
  classes?: string
  iconOnly?: boolean
}

const Button: React.FC<ButtonProps> = ({ name, active, label, onClick, disabled, classes, iconOnly }) => {
  const iconClasses = iconOnly
    ? `button button-medium icon-only full-width mb-0 ${active ? "button-secondary" : "button-subtle"}`
    : ""

  return (
    <button className={cn(name, classes, iconClasses, { active, disabled })} onClick={onClick} type="button">
      {iconOnly ? (
        Icons[name] ? (
          React.createElement(Icons[name])
        ) : (
          ""
        )
      ) : (
        <>
          <div className="image">{Icons[name] ? React.createElement(Icons[name]) : ""}</div>
          {label && <div className="name paragraph-2xs center mb-0">{label}</div>}
        </>
      )}
    </button>
  )
}

interface ToggleButtonProps {
  name: string
  label?: string
  disabled?: boolean
  activeObject: SignObjectState
  onUpdate: (value: Partial<SignTextState>) => void
}

const ToggleButton: React.FC<ToggleButtonProps> = ({ name, disabled, label, activeObject, onUpdate }) => {
  const active: boolean = activeObject[name]

  const onChange = () => {
    if (disabled) {
      return
    }

    const state: Partial<SignTextState> = {}
    state[name] = !active
    onUpdate(state)
  }

  return (
    <Button
      name={name}
      active={active}
      onClick={onChange}
      label={label}
      disabled={disabled}
      classes="object_button no-hover-mobile no-focus"
      iconOnly
    />
  )
}

interface TextToolboxStateProps {
  data: SignData | null
  signType: string
  subToolbox: string | null
  signState: SignState | null
  activeObject: SignObjectState | null
}

interface TextToolboxDispatchProps {
  setToolbox: typeof actions.setToolbox
  setSubToolbox: typeof actions.setSubToolbox
  setSubTarget: typeof actions.setSubTarget
  setToolboxHeight: typeof actions.setToolboxHeight
}

type TextToolboxProps = TextToolboxStateProps & TextToolboxDispatchProps & ToolboxStripItemProps & WithTranslation

interface TextToolboxState {
  emojiUsed: boolean
  missingGlyphs: boolean
  size: string
  text: string
  activeTab: string
}

class TextToolbox extends React.Component<TextToolboxProps, TextToolboxState> {
  readonly state: TextToolboxState
  textbox = React.createRef<HTMLTextAreaElement>()
  previousObject: symbol | null = null

  debouncedUpdateGlyphsPresent = debounce(
    (present: boolean) => {
      this.setState({ missingGlyphs: !present })
    },
    500,
    { leading: false }
  )

  constructor(props: TextToolboxProps) {
    super(props)

    let size = 0
    let text = ""

    if (props.activeObject && Sign.isText(props.activeObject)) {
      size = props.activeObject.size
      text = props.activeObject.text
    }

    this.state = {
      emojiUsed: false,
      missingGlyphs: false,
      size: size.toFixed(1).toString(),
      text,
      activeTab: props.activeObject && Sign.isText(props.activeObject) ? "active-text" : "all-objects",
    }
  }

  componentDidMount() {
    const { sign } = this.props
    sign.onActiveChanged.subscribe(this.updateObjectFromSignState)
    sign.onEmojiUsed.subscribe(this.setEmojiUsed)
    sign.onGlyphsPresent.subscribe(this.debouncedUpdateGlyphsPresent)
    this.exitEditing()
  }

  componentWillUnmount() {
    this.props.sign.onActiveChanged.unsubscribe(this.updateObjectFromSignState)
    this.props.sign.onEmojiUsed.unsubscribe(this.setEmojiUsed)
    this.props.sign.onGlyphsPresent.unsubscribe(this.debouncedUpdateGlyphsPresent)
    this.debouncedUpdateGlyphsPresent.cancel()
  }

  componentDidUpdate() {
    const active = this.props.activeObject

    if (active && active.identity !== this.previousObject && this.textbox.current) {
      this.textbox.current.focus()
    }

    this.previousObject = active ? active.identity : null
  }

  setEmojiUsed = () => {
    this.setState({ emojiUsed: true })
  }

  updateObjectFromSignState = (state: SignObjectState | null) => {
    if (state && this.state.activeTab === "all-objects" && Sign.isText(state)) {
      this.changeActiveTab("active-text")
    }

    let size = this.state.size
    let text = this.state.text

    if (state && Sign.isText(state)) {
      size = state.size.toFixed(1).toString()
      text = state.text
    }

    if (text === this.state.text && size === this.state.size) {
      return
    }

    this.setState({ size, text })
  }

  updateObject = (state: Partial<SignTextState>) => {
    this.props.sign.updateObjectState(state)
  }

  updateSize = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ size: e.target.value })
    this.updateObject({ size: parseFloat(e.target.value.replace(",", ".")) || 1 })
  }

  updateSizeState = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ size: e.target.value })
  }

  submitSize = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      this.updateObject({ size: parseFloat(this.state.size.replace(",", ".")) || 1 })
    }
  }

  updateText = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value
    const textObj = this.props.sign.activeObject as SignText

    if (textObj.isPlaceholder) {
      value = value.replace(textObj.placeholder, "").trim()
      textObj.opacity = 1
      textObj.isPlaceholder = false
      textObj.placeholder = ""
    }

    this.setState({ text: value })
    this.updateObject({ text: value })
  }

  changeActiveTab = (value: string) => {
    this.setState({ activeTab: value })
  }

  toggleMirrored = () => {
    if (!this.props.activeObject) {
      return
    }

    this.updateObject({ mirrored: !this.props.activeObject.mirrored })
  }

  exitEditing = (e?: React.FocusEvent<HTMLTextAreaElement>) => {
    this.props.sign.exitInlineEditing()

    if (e) {
      const textarea = e.target
      textarea.selectionStart = textarea.selectionEnd = textarea.value.length
    }
  }

  selectedStart = (e?: React.FocusEvent<HTMLTextAreaElement>) => {
    this.props.sign.exitInlineEditing()

    if (e) {
      const textarea = e.target
      textarea.selectionStart = textarea.selectionEnd = 0
    }
  }

  onFocus = (e?: React.FocusEvent<HTMLTextAreaElement>) => {
    const textObj = this.props.sign.activeObject as SignText

    return textObj.isPlaceholder ? this.selectedStart(e) : this.exitEditing(e)
  }

  activeTab() {
    const { activeObject } = this.props

    if (this.state.activeTab === "all-objects" || !activeObject) {
      return this.allObjectsTab()
    } else if (this.state.activeTab === "active-text") {
      return this.activeTextTab()
    } else if (this.state.activeTab === "text-content") {
      return this.textContentTab()
    } else if (this.state.activeTab === "font-name") {
      return this.fontNameTab()
    } else if (this.state.activeTab === "font-size") {
      return this.fontSizeTab()
    } else if (this.state.activeTab === "color") {
      return this.colorTab()
    } else if (this.state.activeTab === "alignments") {
      return this.alignementTab()
    } else if (this.state.activeTab === "border") {
      return this.borderTab()
    }

    return this.allObjectsTab()
  }

  topBar = (backTo: string, header: string) => {
    const { sign, t } = this.props

    return (
      <div className="ml-3 flex h-12 items-center">
        <button
          className="toolbox-back button button-subtle icon-left mb-0 px-2"
          onClick={() => {
            this.changeActiveTab(backTo)
            if (backTo == "all-objects") {
              sign.setObjectActive(null)
            }
            if (isTablet()) {
              this.props.setToolboxHeight(288)
            }
          }}
          type="button"
        >
          <ChevronLeftLarge className="!mr-1 h-6 w-6" />
          <span className="text-sm font-semibold">{t("toolbox.back")}</span>
        </button>
        <span className="absolute left-1/2 -translate-x-1/2 text-base font-semibold">{header}</span>
      </div>
    )
  }

  activeTextTab() {
    const { activeObject, data, signType, sign, signState, t } = this.props

    if (!activeObject || !Sign.isText(activeObject) || !data) {
      return null
    }

    const objects = (signState ? signState.objects : []).filter(o => Sign.isText(o))
    const index = objects.indexOf(activeObject)

    const allowedStyles = Fonts.allowedStyles(activeObject.font, this.props.sign.fontdata)
    const customFonts = this.props.sign.fontdata.sets["custom"]
    const isCustom = customFonts.includes(activeObject.font)
    const currentFont = isCustom ? `(Admin only) ${activeObject.font}` : activeObject.font

    return (
      <>
        {this.topBar("all-objects", t("toolbox.text_object") + " " + (index + 1))}

        <Scrollable>
          <div className="object-settings-content">
            <div
              className="object-option-row add-text"
              onClick={() => {
                this.changeActiveTab("text-content")
                if (isTablet()) {
                  // 186 (heigth of text content edit toolbox) - 186 (offset to get the sign at about the correct height)
                  this.props.setToolboxHeight(0)
                }
              }}
            >
              <span className="left paragraph paragraph-sm bold mb-0" style={{ whiteSpace: "nowrap", paddingRight: 4 }}>
                {t("toolbox.change_text")}
              </span>
              <span className="right">
                {activeObject.text === "" ? (
                  <span className="enter-text-here">
                    <span className="paragraph paragraph-sm mb-0">{t("signomatic.your_text_here")}</span>
                    <Blinker />
                  </span>
                ) : (
                  <span className="paragraph paragraph-sm sign-text-value data-hj-suppress mb-0">
                    {activeObject.text}
                  </span>
                )}
                <span className="chevron">
                  <ChevronRightLarge />
                </span>
              </span>
            </div>

            <div className="textinput-wrapper">
              <div className="toggle-buttons">
                <ToggleButton
                  name="bold"
                  activeObject={activeObject}
                  onUpdate={this.updateObject}
                  disabled={!allowedStyles.bold || (activeObject.italic && !allowedStyles.boldItalic)}
                />
                <ToggleButton
                  name="italic"
                  activeObject={activeObject}
                  onUpdate={this.updateObject}
                  disabled={!allowedStyles.italic || (activeObject.bold && !allowedStyles.boldItalic)}
                />
                <ToggleButton
                  name="underlined"
                  activeObject={activeObject}
                  onUpdate={this.updateObject}
                  disabled={Fonts.disableUnderline(activeObject.font, signType)}
                />
                <Button
                  name="justify_left"
                  active={activeObject.justify === "left"}
                  onClick={() => this.updateObject({ justify: "left" })}
                  classes="object_button no-hover-mobile no-focus"
                  iconOnly
                />
                <Button
                  name="justify_center"
                  active={activeObject.justify === "center"}
                  onClick={() => this.updateObject({ justify: "center" })}
                  classes="object_button no-hover-mobile no-focus"
                  iconOnly
                />
                <Button
                  name="justify_right"
                  active={activeObject.justify === "right"}
                  onClick={() => this.updateObject({ justify: "right" })}
                  classes="object_button no-hover-mobile no-focus"
                  iconOnly
                />
              </div>
            </div>

            <div className="object-option-row font-name" onClick={() => this.changeActiveTab("font-name")}>
              <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.font")}</span>
              <span className="right">
                <span className="paragraph paragraph-sm font-name mb-0">{currentFont}</span>
                <span className="chevron">
                  <ChevronRightLarge />
                </span>
              </span>
            </div>
            <div className="object-option-row font-size" onClick={() => this.changeActiveTab("font-size")}>
              <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.text_size")}</span>
              <span className="right">
                <span className="paragraph paragraph-sm mb-0">{activeObject.size}</span>
                <span className="chevron">
                  <ChevronRightLarge />
                </span>
              </span>
            </div>
            {(data.multicolor || data.transparent) && (
              <div className="object-option-row color" onClick={() => this.changeActiveTab("color")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.color")}</span>
                <span className="right">
                  <span className="paragraph paragraph-sm mb-0">
                    {t(`background_colors.${activeObject.color}`, { defaultValue: activeObject.color })}
                  </span>
                  <span
                    className={cn("color-swatch", ColorUtil.colorClass(activeObject.color))}
                    style={{ backgroundColor: activeObject.color }}
                  ></span>
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
            )}
            <div className="object-option-row alignment" onClick={() => this.changeActiveTab("alignments")}>
              <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.alignment")}</span>
              <span className="right">
                <span className="chevron">
                  <ChevronRightLarge />
                </span>
              </span>
            </div>
            {data.object_borders && (
              <div className="object-option-row border-type" onClick={() => this.changeActiveTab("border")}>
                <span className="left paragraph paragraph-sm bold mb-0">{t("toolbox.border")}</span>
                <span className="right">
                  <span className="paragraph paragraph-sm mb-0">
                    {activeObject.border ? t(`shape_type.${activeObject.border.shape}`) : t("shape_type.none")}
                  </span>
                  <span className="border-icon">
                    {activeObject.border
                      ? React.createElement(ShapeIcons[activeObject.border.shape])
                      : React.createElement(ShapeIcons["none"])}
                  </span>
                  <span className="chevron">
                    <ChevronRightLarge />
                  </span>
                </span>
              </div>
            )}
          </div>
          <div className="actions">
            <div className="paragraph paragraph-sm bold mb-0">{t("toolbox.actions")}</div>
            <span className="buttons">
              <button
                className="duplicate button button-secondary mb-0"
                onClick={() => {
                  sign.duplicateObject(activeObject, sign.centerX, sign.centerY)
                }}
                type="button"
              >
                <DuplicateOutline />
                {t("misc.duplicate")}
              </button>
              <button
                className="delete button button-secondary button-error mb-0"
                onClick={e => {
                  e.stopPropagation()
                  sign.deleteObject(activeObject)
                }}
                type="button"
              >
                <TrashOutline />
                {t("misc.delete")}
              </button>
            </span>
          </div>
        </Scrollable>
      </>
    )
  }

  textContentTab() {
    return (
      <>
        {this.topBar("active-text", this.props.t("toolbox.change_text"))}
        <div className="text-edit">
          <textarea
            name="sign_text"
            id="sign_text"
            className="mousetrap data-hj-suppress"
            placeholder={this.props.t("signomatic.your_text_here")}
            value={this.state.text}
            onChange={this.updateText}
            onFocus={this.onFocus}
            ref={this.textbox}
            autoFocus
          />
        </div>
      </>
    )
  }

  fontNameTab() {
    const { data, activeObject, t } = this.props
    const customFonts = this.props.sign.fontdata.sets["custom"]

    if (!activeObject || !data) {
      return
    }

    const active: FontItemProps = {
      label: activeObject.font,
      value: activeObject.font.toLowerCase(),
      custom: customFonts.includes(activeObject.font),
      selected: true,
    }

    const fontSet = Fonts.enabledFonts(
      this.props.signType,
      this.props.sign.fontdata,
      this.props.sign.data.font_set || ""
    )

    const fonts: FontItemProps[] =
      fontSet.map(font => ({
        label: font,
        value: font,
        selected: font.toLowerCase() === activeObject.font.toLowerCase(),
        custom: customFonts.includes(font),
      })) || []

    return (
      <>
        {this.topBar("active-text", t("toolbox.font"))}
        <div className="font-name-tab flex flex-1">
          <div className="items flex flex-1">
            <Scrollable>
              <FontGroup label={t("toolbox.selected_font")} items={[active]} />
              <FontGroup
                label={t("toolbox.all_fonts")}
                items={fonts}
                onClick={value => this.updateObject({ font: value })}
                classes="all-fonts"
              />
            </Scrollable>
          </div>
        </div>
      </>
    )
  }

  fontSizeTab() {
    const { signState, data, activeObject, t } = this.props

    if (!signState || !data || !activeObject) {
      return
    }

    const size = this.state.size
    const min = currentMinText(data, signState, activeObject.font)

    return (
      <>
        {this.topBar("active-text", t("toolbox.text_size"))}
        <div className="font-size-tab">
          <div className="object-size">
            <div className="items">
              <Slider
                inputId="font_size"
                inputName="font_size_range"
                label={t("toolbox.text_size")}
                ariaLabelInput={t("toolbox.text_size_input")}
                min={min}
                max={Math.min(data.max_size.h, signState.height)}
                rangeMinIcon={TextSmallIcon}
                rangeMaxIcon={TextLargeIcon}
                value={parseFloat(size)}
                onChange={value => this.updateObject({ size: value })}
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  colorTab() {
    const { sign, setSubTarget, t } = this.props
    setSubTarget("object")
    return (
      <>
        {this.topBar("active-text", t("toolbox.text_colour"))}
        <div className="object-color-picker">
          <ColorPicker sign={sign} />
        </div>
      </>
    )
  }

  alignementTab() {
    const { sign, activeObject, data, t } = this.props

    if (!data || !activeObject) {
      return
    }

    const showReversed = data.transparent || data.name == "sticker"

    return (
      <>
        {this.topBar("active-text", t("toolbox.alignment"))}
        <div className="object-alignements">
          <div className="center-buttons">
            <Button
              name="align_center"
              active={activeObject.top === 0 && activeObject.left === 0}
              onClick={() => this.updateObject({ top: 0, left: 0 })}
              label={t("toolbox.center_center")}
              classes="alignement-button"
            />
            <Button
              name="align_vertical"
              active={activeObject.left !== 0 && activeObject.top === 0}
              onClick={() => this.updateObject({ top: 0 })}
              label={t("toolbox.center_vertical")}
              classes="alignement-button"
            />
            <Button
              name="align_horizontal"
              active={activeObject.left === 0 && activeObject.top !== 0}
              onClick={() => this.updateObject({ left: 0 })}
              label={t("toolbox.center_horizontal")}
              classes="alignement-button"
            />
          </div>
          <div className="center-buttons">
            <Button
              name="arrange_front"
              onClick={() => sign.arrangeObject(activeObject, "front")}
              label={t("toolbox.arrange_front")}
              classes="alignement-button"
            />
            <Button
              name="arrange_forward"
              onClick={() => sign.arrangeObject(activeObject, "forward")}
              label={t("toolbox.arrange_forward")}
              classes="alignement-button"
            />
            <Button
              name="arrange_backward"
              onClick={() => sign.arrangeObject(activeObject, "backward")}
              label={t("toolbox.arrange_backward")}
              classes="alignement-button"
            />
            {showReversed && (
              <Button
                name="mirrored"
                active={activeObject.mirrored}
                onClick={() => this.toggleMirrored()}
                label={t("toolbox.reversed")}
                classes="alignement-button"
              />
            )}
          </div>
        </div>
      </>
    )
  }

  borderTab() {
    return (
      <>
        {this.topBar("active-text", this.props.t("toolbox.border"))}
        {window.innerWidth <= 1199 ? (
          <div className="object-border-picker">
            <ObjectBorderPicker sign={this.props.sign} />
          </div>
        ) : (
          <Scrollable>
            <div className="object-border-picker">
              <ObjectBorderPicker sign={this.props.sign} />
            </div>
          </Scrollable>
        )}
      </>
    )
  }

  hints() {
    const { signType, setToolbox } = this.props

    if (this.state.missingGlyphs) {
      return <Hint show type="errormessage" translation="toolbox.hint_missing_glyph" />
    }

    if (this.state.emojiUsed) {
      const link = (
        <a
          onClick={e => {
            e.preventDefault()
            setToolbox("image")
          }}
        >
          image archive
        </a>
      )

      return <Hint show type="tip" translation="toolbox.hint_emoji" components={{ a: link }} />
    }

    const showHint = metaNameSign(signType) || this.state.text.includes("\n")
    return <Hint show={showHint} type="tip" translation="toolbox.hint_multiple_objects" />
  }

  allObjectsTab() {
    const { sign, data, signState, t } = this.props

    if (!data || !signState) {
      return
    }

    const objects = (signState ? signState.objects : []).filter(Sign.isText)

    return (
      <Scrollable>
        <div className="all-objects">
          <div className="objects">
            <h5>{t("toolbox.all_texts")}</h5>
            {!(data.single_object && objects.length > 0) && (
              <button
                id="btn_add_text"
                className="button button-secondary full-width"
                onClick={() => {
                  this.changeActiveTab("active-text")
                  sign.createObject("text").then(text => {
                    if (text) {
                      sign.setObjectActive(text.state)
                    }
                  })
                }}
                type="button"
              >
                <PlusLarge />
                {t("toolbox.add_new_text")}
              </button>
            )}
            <div className="divider"></div>
            {objects.map((obj, i) => {
              if (signState.doubleSided && obj.side !== signState.side) {
                return null
              }

              return (
                <div
                  key={i}
                  className="object-button"
                  onClick={() => {
                    sign.setObjectActive(obj)
                    this.changeActiveTab("active-text")
                  }}
                >
                  <div className="paragraph-2xs">{t("toolbox.text_object") + " " + (i + 1)}</div>
                  <div
                    className={cn("paragraph sign-text-value data-hj-suppress mb-0", {
                      bold: obj.bold,
                      placeholder: !obj.text,
                    })}
                  >
                    {obj.text || t("signomatic.your_text_here")}
                  </div>
                </div>
              )
            })}
          </div>
          {this.hints()}
        </div>
      </Scrollable>
    )
  }

  render() {
    const { signState, activeObject } = this.props

    if (signState && signState.doubleSided && activeObject && activeObject.side !== signState.side) {
      return null
    }

    return (
      <Toolbox className={cn(this.state.activeTab)} tKey="text" hideHeaderBar={this.state.activeTab != "all-objects"}>
        <div id="toolbox_text" className="object-toolbox text h-full">
          <div className="object-settings">{this.activeTab()}</div>
        </div>
      </Toolbox>
    )
  }
}

export default connect(
  ({ signType, signState, currentSignData, activeObject, subToolbox }: State): TextToolboxStateProps => ({
    signType,
    signState,
    activeObject,
    subToolbox,
    data: currentSignData,
  }),
  {
    setToolbox: actions.setToolbox,
    setSubToolbox: actions.setSubToolbox,
    setSubTarget: actions.setSubTarget,
    setToolboxHeight: actions.setToolboxHeight,
  }
)(withTranslation()(TextToolbox))
